import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  TextField
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface LlmActivityRecord {
  id: number;
  llm_model_id: number;
  model_name: string;
  provider: string;
  system_prompt: string | null;
  prompt: string;
  response_body: string | null;
  error_message: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_tokens: number | null;
  duration_ms: number | null;
  estimated_cost_usd: number | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface LlmActivityDetailDialogProps {
  record: LlmActivityRecord;
  open: boolean;
  onClose: () => void;
}

export const LlmActivityDetailDialog: React.FC<LlmActivityDetailDialogProps> = ({
  record,
  open,
  onClose
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">LLM Activity Details (ID: {record.id})</Typography>
          <Chip
            label={record.status}
            color={
              record.status === 'completed' ? 'success' :
              record.status === 'failed' ? 'error' :
              'default'
            }
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Metadata Section */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Metadata
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">ID</Typography>
                <Typography variant="body2">{record.id}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Model ID</Typography>
                <Typography variant="body2">{record.llm_model_id}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Model Name</Typography>
                <Typography variant="body2">{record.model_name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Provider</Typography>
                <Typography variant="body2">{record.provider}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="body2">{record.status}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created At</Typography>
                <Typography variant="body2">{formatDate(record.created_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Updated At</Typography>
                <Typography variant="body2">{formatDate(record.updated_at)}</Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Usage Metrics Section */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Usage Metrics
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Prompt Tokens</Typography>
                <Typography variant="body2">
                  {record.prompt_tokens !== null ? record.prompt_tokens.toLocaleString() : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Completion Tokens</Typography>
                <Typography variant="body2">
                  {record.completion_tokens !== null ? record.completion_tokens.toLocaleString() : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Tokens</Typography>
                <Typography variant="body2">
                  {record.total_tokens !== null ? record.total_tokens.toLocaleString() : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Estimated Cost</Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  {record.estimated_cost_usd !== null ? `$${record.estimated_cost_usd.toFixed(6)} USD` : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2">
                  {record.duration_ms !== null ? `${record.duration_ms}ms` : '-'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* System Prompt Section */}
          {record.system_prompt && (
            <>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  System Prompt
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={record.system_prompt}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Box>
              <Divider />
            </>
          )}

          {/* Prompt Section */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Prompt
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={record.prompt}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
            />
          </Box>

          <Divider />

          {/* Response Section */}
          {record.response_body && (
            <>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Response Body
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={record.response_body}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Box>
              <Divider />
            </>
          )}

          {/* Error Section */}
          {record.error_message && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
                Error Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={record.error_message}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                error
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<Close />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
